import { resolveCmsDevProxyUrl } from './cms-dev-proxy-url';

type CachedAsset = {
  objectUrl: string;
  blob: Blob;
};

export type AssetPrefetchOptions = {
  signal?: AbortSignal;
};

const cachedAssets = new Map<string, CachedAsset>();
const downloadingAssets = new Map<string, Promise<string>>();

export function getObjectUrl(url: string): string | undefined {
  return cachedAssets.get(resolveFetchUrl(url))?.objectUrl;
}

export function isPrefetched(url: string): boolean {
  return cachedAssets.has(resolveFetchUrl(url));
}

export function getCachedAssetURLIfExists(url: string): string {
  return cachedAssets.get(resolveFetchUrl(url))?.objectUrl ?? url;
}

export async function prefetch(url: string, options: AssetPrefetchOptions = {}): Promise<string> {
  const fetchUrl = resolveFetchUrl(url);

  const cached = cachedAssets.get(fetchUrl);
  if (cached) {
    return cached.objectUrl;
  }

  const pending = downloadingAssets.get(fetchUrl);
  if (pending) {
    return pending;
  }

  const download = downloadToObjectUrl(fetchUrl, options.signal);
  downloadingAssets.set(fetchUrl, download);

  try {
    return await download;
  } finally {
    downloadingAssets.delete(fetchUrl);
  }
}

export function revoke(url: string): void {
  const fetchUrl = resolveFetchUrl(url);
  const entry = cachedAssets.get(fetchUrl);
  if (!entry) return;
  URL.revokeObjectURL(entry.objectUrl);
  cachedAssets.delete(fetchUrl);
}

export function revokeAll(): void {
  for (const entry of cachedAssets.values()) {
    URL.revokeObjectURL(entry.objectUrl);
  }
  cachedAssets.clear();
  downloadingAssets.clear();
}

function resolveFetchUrl(url: string): string {
  return resolveCmsDevProxyUrl(url) ?? url;
}

async function downloadToObjectUrl(fetchUrl: string, signal?: AbortSignal): Promise<string> {
  const response = await fetch(fetchUrl, { signal });
  if (!response.ok) {
    throw new Error(`Asset prefetch failed (${response.status}): ${fetchUrl}`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  cachedAssets.set(fetchUrl, { objectUrl, blob });
  return objectUrl;
}
